import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface ConstellationDef {
  name: string;
  stars: [number, number][];
  lines: [number, number][];
  offset: [number, number, number];
  scale: number;
  color: string;
}

const constellations: ConstellationDef[] = [
  {
    name: 'Marilyn Monroe',
    color: '#ffb0d0',
    offset: [40, 20, -50],
    scale: 4.5,
    stars: [
      // Hair flowing right
      [0, 4.2], [0.8, 4.8], [1.6, 5.0], [2.2, 4.6], [2.4, 3.8],
      // Left hair curl
      [-0.6, 4.6], [-1.2, 4.8], [-1.6, 4.4], [-1.4, 3.6],
      // Face outline
      [-1.0, 3.2], [-0.8, 2.4], [-0.4, 1.8], [0, 1.6], [0.4, 1.8], [0.8, 2.4], [1.0, 3.2],
      // Eyes
      [-0.5, 3.0], [0.5, 3.0],
      // Lips (iconic)
      [-0.3, 2.2], [0, 2.0], [0.3, 2.2],
      // Shoulders & dress
      [-1.4, 1.2], [-1.0, 1.4], [0, 1.4], [1.0, 1.4], [1.4, 1.2],
      // Iconic pose arm
      [-1.6, 0.6], [1.6, 0.6],
      // Beauty mark
      [0.6, 2.6],
    ],
    lines: [
      // Hair right
      [0, 1], [1, 2], [2, 3], [3, 4],
      // Hair left
      [0, 5], [5, 6], [6, 7], [7, 8],
      // Face left
      [8, 9], [9, 10], [10, 11],
      // Chin
      [11, 12], [12, 13],
      // Face right
      [13, 14], [14, 4],
      // Shoulders
      [21, 22], [22, 23], [23, 24], [24, 25],
      // Arms
      [21, 26], [25, 27],
      // Lips
      [19, 20], [20, 21 - 2],
    ],
  },
  {
    name: 'Muhammad Ali',
    color: '#ffd080',
    offset: [-45, 25, -40],
    scale: 4.5,
    stars: [
      // Head top
      [0, 5.0], [0.6, 5.2], [-0.6, 5.2],
      // Face sides
      [-0.8, 4.4], [-0.9, 3.6], [-0.7, 2.8],
      [0.8, 4.4], [0.9, 3.6], [0.7, 2.8],
      // Chin (strong jaw)
      [-0.4, 2.4], [0, 2.2], [0.4, 2.4],
      // Eyes (intense)
      [-0.4, 4.0], [0.4, 4.0],
      // Nose
      [0, 3.4],
      // Boxing stance - left arm (jab)
      [-1.2, 2.0], [-1.8, 1.6], [-2.6, 1.4], [-3.4, 1.2], [-4.0, 1.2],
      // Glove
      [-4.4, 1.4], [-4.4, 1.0],
      // Right arm (guard)
      [1.2, 2.0], [1.4, 1.6], [1.2, 1.2],
      // Torso
      [-0.8, 1.6], [0, 1.0], [0.8, 1.6],
      // Legs (stance)
      [-0.4, 0.4], [0.4, 0.4], [-0.6, -0.2], [0.6, -0.2],
    ],
    lines: [
      // Head
      [2, 0], [0, 1], [2, 3], [3, 4], [4, 5], [5, 9],
      [1, 6], [6, 7], [7, 8], [8, 11],
      // Chin
      [9, 10], [10, 11],
      // Left arm jab
      [15, 16], [16, 17], [17, 18], [18, 19],
      // Glove
      [19, 20], [19, 21],
      // Right arm
      [22, 23], [23, 24],
      // Torso
      [5, 25], [8, 27], [25, 26], [26, 27],
      // Shoulders to arms
      [25, 15], [27, 22],
      // Legs
      [26, 28], [26, 29], [28, 30], [29, 31],
    ],
  },
  {
    name: 'Michael Jackson',
    color: '#c0c0ff',
    offset: [50, -15, -45],
    scale: 4.5,
    stars: [
      // Hat top
      [-0.8, 5.6], [0, 5.8], [0.8, 5.6],
      // Hat brim
      [-1.2, 5.2], [-0.4, 5.2], [0.4, 5.2], [1.2, 5.2],
      // Face
      [-0.7, 4.6], [0.7, 4.6], [-0.5, 3.8], [0.5, 3.8],
      // Chin
      [0, 3.4],
      // Torso (leaning)
      [0, 2.8], [-0.6, 2.0], [0.6, 2.0],
      // Right arm (pointing up - iconic)
      [1.0, 2.4], [1.6, 3.0], [2.0, 3.8], [2.2, 4.4],
      // Glove star (bright)
      [2.3, 4.8],
      // Left arm
      [-1.0, 2.4], [-1.4, 1.8],
      // Legs (tip-toe stance)
      [-0.4, 1.2], [0.4, 1.2],
      // Feet (on toes!)
      [-0.5, 0.4], [0.3, 0.6], [0.5, 0.2],
      // Knee bend
      [-0.2, 0.8], [0.2, 0.8],
    ],
    lines: [
      // Hat
      [0, 1], [0, 2], [3, 4], [4, 5], [5, 6],
      // Hat to face
      [3, 7], [6, 8],
      // Face
      [7, 9], [8, 10], [9, 11], [10, 11],
      // Torso
      [11, 12], [12, 13], [12, 14],
      // Right arm pointing up
      [14, 15], [15, 16], [16, 17], [17, 18], [18, 19],
      // Left arm
      [13, 20], [20, 21],
      // Legs
      [13, 22], [14, 23],
      // Lower legs
      [22, 27], [23, 28], [27, 24], [28, 25], [25, 26],
    ],
  },
  {
    name: 'Prince',
    color: '#d0a0ff',
    offset: [-40, -20, -55],
    scale: 4.5,
    stars: [
      // Hair (wild/flowing)
      [-1.0, 5.4], [-0.4, 5.8], [0.4, 5.6], [1.0, 5.2],
      [1.4, 4.8], [-1.2, 4.8],
      // Face
      [-0.7, 4.2], [0.7, 4.2], [-0.5, 3.6], [0.5, 3.6],
      // Chin
      [0, 3.2],
      // Guitar neck (going left and up)
      [-1.4, 3.0], [-2.2, 3.4], [-3.0, 3.8], [-3.6, 4.2],
      // Guitar body (held at torso)
      [-0.6, 2.0], [-1.2, 1.6], [-1.4, 1.0], [-0.8, 0.8], [-0.2, 1.2],
      // Right arm on guitar
      [0.8, 2.6], [1.0, 2.0],
      // Torso
      [0, 2.6], [-0.4, 2.6],
      // Legs
      [-0.3, 0.2], [0.3, 0.2],
      // Heeled boots (iconic)
      [-0.5, -0.4], [0.5, -0.4],
      // Love symbol top circle star
      [0, 6.2],
    ],
    lines: [
      // Hair
      [5, 0], [0, 1], [1, 2], [2, 3], [3, 4],
      // Face
      [5, 6], [4, 7], [6, 8], [7, 9], [8, 10], [9, 10],
      // Guitar neck
      [10, 11], [11, 12], [12, 13], [13, 14],
      // Guitar body
      [15, 16], [16, 17], [17, 18], [18, 19], [19, 15],
      // Arms
      [7, 20], [20, 21],
      // Torso
      [10, 22], [22, 23],
      // To guitar
      [23, 15],
      // Legs
      [19, 24], [22, 25],
      // Boots
      [24, 26], [25, 27],
      // Love symbol hint
      [1, 28],
    ],
  },
];

function projectToSphere(
  x: number, y: number,
  offset: [number, number, number],
  scale: number
): THREE.Vector3 {
  const px = offset[0] + x * scale;
  const py = offset[1] + y * scale;
  const pz = offset[2];
  return new THREE.Vector3(px, py, pz);
}

function ConstellationStars({ def }: { def: ConstellationDef }) {
  const positions = useMemo(() => {
    const arr = new Float32Array(def.stars.length * 3);
    def.stars.forEach(([x, y], i) => {
      const pos = projectToSphere(x, y, def.offset, def.scale);
      arr[i * 3] = pos.x;
      arr[i * 3 + 1] = pos.y;
      arr[i * 3 + 2] = pos.z;
    });
    return arr;
  }, [def]);

  const geometry = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    return geo;
  }, [positions]);

  return (
    <points geometry={geometry}>
      <pointsMaterial
        size={0.4}
        color={def.color}
        transparent
        opacity={0.9}
        sizeAttenuation
        depthWrite={false}
      />
    </points>
  );
}

function ConstellationLines({ def }: { def: ConstellationDef }) {
  const linePositions = useMemo(() => {
    const pts: number[] = [];
    def.lines.forEach(([a, b]) => {
      if (a < def.stars.length && b < def.stars.length) {
        const pa = projectToSphere(def.stars[a][0], def.stars[a][1], def.offset, def.scale);
        const pb = projectToSphere(def.stars[b][0], def.stars[b][1], def.offset, def.scale);
        pts.push(pa.x, pa.y, pa.z, pb.x, pb.y, pb.z);
      }
    });
    return new Float32Array(pts);
  }, [def]);

  const geometry = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(linePositions, 3));
    return geo;
  }, [linePositions]);

  return (
    <lineSegments geometry={geometry}>
      <lineBasicMaterial
        color={def.color}
        transparent
        opacity={0.15}
        depthWrite={false}
      />
    </lineSegments>
  );
}

export default function Constellations() {
  const groupRef = useRef<THREE.Group>(null);

  useFrame((_, delta) => {
    if (groupRef.current) {
      groupRef.current.rotation.y += delta * 0.005;
      groupRef.current.rotation.x += delta * 0.002;
    }
  });

  return (
    <group ref={groupRef}>
      {constellations.map((def) => (
        <group key={def.name}>
          <ConstellationStars def={def} />
          <ConstellationLines def={def} />
        </group>
      ))}
    </group>
  );
}
